/**
 * DraftFindingCard -- Displays a single draft finding with confirm/discard/edit actions
 *
 * When isEditing is true, renders FindingInlineEditor instead of the normal card.
 */

import { Button } from "@/components/ui";
import {
  useConfirmFinding,
  useDiscardFinding,
} from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import {
  useSeverityConfig,
  useStatusConfig,
} from "../../findings/findings-constants";
import { FindingInlineEditor } from "./FindingInlineEditor";

interface DraftFinding {
  _id: Id<"findings">;
  title: string;
  observation: string;
  recommendation: string;
  severity: "critical" | "major" | "minor" | "observation";
  status: string;
}

interface DraftFindingCardProps {
  finding: DraftFinding;
  isEditing: boolean;
  onEdit: () => void;
  onEditSave: () => void;
  onEditCancel: () => void;
}

export function DraftFindingCard({
  finding,
  isEditing,
  onEdit,
  onEditSave,
  onEditCancel,
}: DraftFindingCardProps) {
  const { t } = useTranslation("audits");
  const confirmMutation = useConfirmFinding();
  const discardMutation = useDiscardFinding();
  const severityConfig = useSeverityConfig();
  const statusConfig = useStatusConfig();
  const colors = severityConfig[finding.severity];

  return (
    <>
      <FindingInlineEditor
        finding={finding}
        isEditing={isEditing}
        onSave={onEditSave}
        onCancel={onEditCancel}
      />
      <div
        className="rounded-md border-2 border-dashed p-3 space-y-2"
        style={{ borderColor: colors.border, backgroundColor: colors.bg }}
      >
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: colors.dot }}
          />
          <span
            className="text-xs font-medium px-1.5 py-0.5 rounded uppercase"
            style={{
              backgroundColor: statusConfig.draft.bg,
              color: statusConfig.draft.text,
            }}
          >
            {statusConfig.draft.label}
          </span>
          <span className="font-medium text-sm flex-1">{finding.title}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">{t("findings.card.observation")} </span>
          {finding.observation}
        </p>
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">
            {t("findings.card.recommendation")}{" "}
          </span>
          {finding.recommendation}
        </p>
        <div className="flex gap-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => confirmMutation.mutateAsync({ id: finding._id })}
            disabled={confirmMutation.isPending}
          >
            {t("findings.card.confirm")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => discardMutation.mutateAsync({ id: finding._id })}
            disabled={discardMutation.isPending}
          >
            {t("findings.card.discard")}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            {t("findings.card.edit")}
          </Button>
        </div>
      </div>
    </>
  );
}
